import React, {useEffect} from 'react';

import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder';
import classroomV2Store from 'client/Classrooms/Classroom/ClassroomV2.store';
import PromptGradeLevelModal from 'client/Classrooms/Classroom/PromptGradeLevelModal/PromptGradeLevelModal.react';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {history} from 'client/history';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import useLocalStorageTimer from 'lib/hooks/useLocalStorageTimer';

interface Props {
  classroomId: string;
}

const LoadedEntryFlows = ({classroomId}: Props) => {
  const classroom = classroomV2Store.getActiveClassroom();
  const hasGrades = !classroom.getGradeLevels().isEmpty();
  const skipTimer = hasGrades || masqueradeStore.isMasquerading();

  const {hasExpired, resetTimer, clearTimer} = useLocalStorageTimer({
    skipTimer,
    // The legacy container omits this; true is the hook's default.
    showOnFirstVisit: true,
    timeLimitHours: 8,
    storageKey: `classroom-${classroom.getName()}-${classroomId}`
  });

  return (
    <>
      {/* !todo license/approval banners omitted for now; re-add as a header ClassNoticeBar later */}
      {hasExpired && !skipTimer && (
        <PromptGradeLevelModal
          classroom={classroom}
          handleResetTimer={resetTimer}
          handleClearTimer={clearTimer}
        />
      )}
    </>
  );
};

/**
 * The class-entry side flows the legacy Classroom container owns: invalid-class
 * exit, approval banners, and the missing-grade-level prompt. The legacy
 * archived toast is intentionally replaced by the banner's Archived chip.
 */
export const ClassDetailEntryFlows = ({classroomId}: Props) => {
  // The classroom store builds legacy-typed queries; the hook only uses the shared runtime API.
  const {isReady, errors} = useAreLatestQueriesReady([
    () => classroomV2Store.getClassroomQuery(classroomId) as unknown as QueryBuilder
  ]);
  const hasInvalidClassroom = isReady && errors.some(Boolean);

  // Mirrors the legacy container's onInvalidQuery exit.
  useEffect(() => {
    if (hasInvalidClassroom) {
      history.pushState(null, '/classes');
    }
  }, [hasInvalidClassroom]);

  if (!isReady || hasInvalidClassroom) {
    return null;
  }
  return <LoadedEntryFlows classroomId={classroomId} />;
};
